// src/components/forums/ForumTopicPreview.tsx

import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare,
  Eye,
  Pin,
  Clock,
  ThumbsUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { formatRelativeTime } from '@/lib/utils/time';
import { useAuth } from '@/contexts/AuthContext';
import {
  getTopicLikeCount,
  getTopicLikeStatus,
  toggleTopicLike,
  getTopicLikeAvatars,
} from '@/lib/supabase/forum';
import ClubBadges from '../shared/ClubBadges';
import type { ClubBadge } from '@/lib/supabase/clubs-utils';
import { supabase } from '@/lib/supabase';

interface ForumTopicPreviewProps {
  topic: {
    id: string;
    title: string;
    views_count: number;
    replies_count: number;
    is_pinned: boolean;
    created_at: string;
    last_reply_at: string;
    author: {
      id: string;          // internal user ID
      full_name: string;
      avatar_url: string;
    };
    last_reply_author?: {
      id: string;          // internal user ID
      full_name: string;
      avatar_url: string;
    };
  };
  onClick: () => void;
}

export const ForumTopicPreview: React.FC<ForumTopicPreviewProps> = ({
  topic,
  onClick,
}) => {
  console.log('EARLY: ForumTopicPreview render. topic=', topic);

  const { isDarkMode } = useTheme();
  const { user }       = useAuth();

  const [authorClubs,      setAuthorClubs]      = useState<ClubBadge[]>([]);
  const [replyAuthorClubs, setReplyAuthorClubs] = useState<ClubBadge[]>([]);

  const [likesCount,   setLikesCount]   = useState(0);
  const [isLiked,      setIsLiked]      = useState(false);
  const [likeAvatars,  setLikeAvatars]  = useState<
    { auth_id: string; full_name: string; avatar_url: string | null }[]
  >([]);

  const [isToggling,   setIsToggling]   = useState(false);
  const [showMenu,     setShowMenu]     = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /* ──────────────────── Author clubs (by internal user.id) ──────────────────── */
  useEffect(() => {
    console.log('USE_EFFECT: authorClubs useEffect firing for author.id=', topic.author.id);
    if (!topic.author.id) {
      console.log('USE_EFFECT: no topic.author.id, skipping club fetch');
      return;
    }

    (async () => {
      try {
        const { data: ucData, error: ucError } = await supabase
          .from('user_clubs')
          .select(`
            club_id (
              id,
              club_name,
              logo
            )
          `)
          .eq('user_id', topic.author.id);

        if (ucError) {
          console.error('Error fetching author’s clubs:', ucError);
          setAuthorClubs([]);
          return;
        }

        console.log(
          `USE_EFFECT: raw user_clubs data for user_id=${topic.author.id}:`,
          ucData
        );

        if (!ucData || ucData.length === 0) {
          console.log(
            `USE_EFFECT: no clubs found for user_id=${topic.author.id}`
          );
          setAuthorClubs([]);
          return;
        }

        const badges: ClubBadge[] = ucData.map((row) => ({
          id:       row.club_id.id,
          name:     row.club_id.club_name,
          logo_url: row.club_id.logo,
        }));
        console.log(
          `USE_EFFECT: mapped ClubBadges for user_id=${topic.author.id}:`,
          badges
        );
        setAuthorClubs(badges);
      } catch (err) {
        console.error('Unexpected error fetching author’s clubs:', err);
        setAuthorClubs([]);
      }
    })();
  }, [topic.author.id]);

  /* ────── Last-reply author clubs (if present), by internal user.id ─────────── */
  useEffect(() => {
    console.log(
      'USE_EFFECT: replyAuthorClubs useEffect firing for last_reply_author.id=',
      topic.last_reply_author?.id
    );
    if (!topic.last_reply_author?.id) {
      console.log('USE_EFFECT: no last_reply_author.id, skipping club fetch');
      return;
    }

    (async () => {
      try {
        const { data: ucData, error: ucError } = await supabase
          .from('user_clubs')
          .select(`
            club_id (
              id,
              club_name,
              logo
            )
          `)
          .eq('user_id', topic.last_reply_author.id);

        if (ucError) {
          console.error('Error fetching last-reply author’s clubs:', ucError);
          setReplyAuthorClubs([]);
          return;
        }

        console.log(
          `USE_EFFECT: raw user_clubs data for last_reply_author.id=${topic.last_reply_author.id}:`,
          ucData
        );

        if (!ucData || ucData.length === 0) {
          console.log(
            `USE_EFFECT: no clubs found for last_reply_author.id=${topic.last_reply_author.id}`
          );
          setReplyAuthorClubs([]);
          return;
        }

        const badges: ClubBadge[] = ucData.map((row) => ({
          id:       row.club_id.id,
          name:     row.club_id.club_name,
          logo_url: row.club_id.logo,
        }));
        console.log(
          `USE_EFFECT: mapped ClubBadges for last_reply_author.id=${topic.last_reply_author.id}:`,
          badges
        );
        setReplyAuthorClubs(badges);
      } catch (err) {
        console.error('Unexpected error fetching last-reply author’s clubs:', err);
        setReplyAuthorClubs([]);
      }
    })();
  }, [topic.last_reply_author?.id]);

  /* ───────────── Click outside to close menu ───────────── */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ─────────────── Load like counts / status ───────────── */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [count, avatars] = await Promise.all([
          getTopicLikeCount(topic.id),
          getTopicLikeAvatars(topic.id),
        ]);
        if (alive) {
          setLikesCount(count);
          setLikeAvatars(avatars);
        }
        if (alive && user) {
          const liked = await getTopicLikeStatus(topic.id, user.id);
          if (alive) setIsLiked(liked);
        }
      } catch (err) {
        console.error('Error loading topic likes:', err);
      }
    })();
    return () => {
      alive = false;
    };
  }, [topic.id, user?.id]);

  /* ────────────────── Optimistic like click ─────────────── */
  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || isToggling) return;

    setIsToggling(true);

    const prevLiked = isLiked;
    const nextLiked = !prevLiked;
    const delta     = prevLiked ? -1 : 1;

    // 1️⃣ instant UI update
    setIsLiked(nextLiked);
    setLikesCount((c) => c + delta);
    setLikeAvatars((prev) => {
      if (nextLiked) {
        const me = {
          auth_id   : user.id,
          full_name : user.full_name ?? user.id,
          avatar_url: user.avatar_url ?? null,
        };
        return [me, ...prev.filter((a) => a.auth_id !== user.id)];
      }
      return prev.filter((a) => a.auth_id !== user.id);
    });

    // 2️⃣ background request
    toggleTopicLike(topic.id, user.id)
      .then(async ({ liked, count }) => {
        setIsLiked(liked);
        setLikesCount(count);
        const avatars = await getTopicLikeAvatars(topic.id);
        setLikeAvatars(avatars);
      })
      .catch(() => {
        // 3️⃣ rollback
        setIsLiked(prevLiked);
        setLikesCount((c) => c - delta);
        setLikeAvatars((prev) =>
          prevLiked
            ? [
                {
                  auth_id   : user.id,
                  full_name : user.full_name ?? user.id,
                  avatar_url: user.avatar_url ?? null,
                },
                ...prev,
              ]
            : prev.filter((a) => a.auth_id !== user.id)
        );
      })
      .finally(() => setIsToggling(false));
  };

  /* ─────────────────────── UI ───────────────────────────── */
  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-xl border p-4 cursor-pointer transition-all',
        'hover:border-gold-400 hover:shadow-lg hover:shadow-dark-900/5',
        isDarkMode ? 'bg-dark-200 border-dark-300' : 'bg-white border-gray-200',
        topic.is_pinned && 'border-gold-400 shadow-lg shadow-dark-900/5'
      )}
    >
      <div className="flex items-start gap-4">
        {/* Author avatar */}
        <img
          src={
            topic.author.avatar_url ||
            'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'
          }
          alt={topic.author.full_name}
          className={cn(
            'w-14 h-14 rounded-full object-cover border',
            isDarkMode ? 'border-dark-400' : 'border-gray-200'
          )}
        />

        {/* Title & meta */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="flex items-center gap-2 mb-2">
            {topic.is_pinned && <Pin className="h-4 w-4 text-gold-400" />}
            <h3
              className={cn(
                'font-medium text-lg truncate',
                isDarkMode ? 'text-white' : 'text-gray-900'
              )}
            >
              {topic.title}
            </h3>
          </div>

          {/* Author name */}
          <div
            className={cn(
              'text-sm mb-1',
              isDarkMode ? 'text-white/70' : 'text-gray-600'
            )}
          >
            Started by {topic.author.full_name}
          </div>

          {/* Club badges */}
          {authorClubs.length > 0 && (
            <div className="mb-2">
              <ClubBadges clubs={authorClubs} size="sm" limit={3} />
            </div>
          )}

          {/* Date */}
          <div
            className={cn(
              'flex items-center text-sm mb-3',
              isDarkMode ? 'text-white/70' : 'text-gray-600'
            )}
          >
            <Clock className="h-3.5 w-3.5 mr-1.5" />
            <span>{formatRelativeTime(topic.created_at)}</span>
          </div>

          {/* Stats row */}
          <div
            className={cn(
              'flex items-center space-x-4 text-sm',
              isDarkMode ? 'text-white/70' : 'text-gray-600'
            )}
          >
            <div className="flex items-center">
              <MessageSquare className="h-4 w-4 mr-1.5" />
              <span>
                {topic.replies_count}{' '}
                {topic.replies_count === 1 ? 'reply' : 'replies'}
              </span>
            </div>

            <div className="flex items-center">
              <Eye className="h-4 w-4 mr-1.5" />
              <span>
                {topic.views_count} {topic.views_count === 1 ? 'view' : 'views'}
              </span>
            </div>

            {/* Like button */}
            <div className="flex items-center gap-1">
              <button
                onClick={handleLike}
                className={cn(
                  'flex items-center gap-1',
                  isLiked
                    ? 'text-gold-400'
                    : 'text-dark-800 hover:text-white',
                  isToggling && 'opacity-50 cursor-wait'
                )}
                disabled={isToggling}
              >
                <ThumbsUp className="h-4 w-4 mr-1" />
                <span>{likesCount}</span>
              </button>

              {likesCount > 0 && (
                <div className="flex -space-x-2 ml-1">
                  {likeAvatars.slice(0, 5).map((u) => (
                    <img
                      key={u.auth_id}
                      src={
                        u.avatar_url ||
                        'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'
                      }
                      alt={u.full_name}
                      title={u.full_name}
                      className={cn(
                        'w-5 h-5 rounded-full border object-cover',
                        isDarkMode ? 'border-dark-200' : 'border-white'
                      )}
                    />
                  ))}

                  {likesCount > likeAvatars.length && (
                    <div
                      className={cn(
                        'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium',
                        isDarkMode
                          ? 'bg-dark-300 text-white'
                          : 'bg-gray-200 text-gray-700'
                      )}
                      title={`${likesCount - likeAvatars.length} more`}
                    >
                      +{likesCount - likeAvatars.length}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Latest reply (desktop) */}
        {topic.last_reply_at && topic.last_reply_author && (
          <div className="hidden sm:flex flex-col items-end text-sm space-y-1">
            <span className={cn(isDarkMode ? 'text-white/70' : 'text-gray-600')}>
              Latest reply
            </span>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className="flex items-center justify-end">
                  <span className="text-sm text-white">
                    {topic.last_reply_author.full_name}
                  </span>
                  {replyAuthorClubs.length > 0 && (
                    <div className="ml-2">
                      <ClubBadges clubs={replyAuthorClubs} size="sm" limit={3} />
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    isDarkMode ? 'text-white/70' : 'text-gray-600',
                    'text-xs'
                  )}
                >
                  {formatRelativeTime(topic.last_reply_at)}
                </div>
              </div>
              <img
                src={
                  topic.last_reply_author.avatar_url ||
                  'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'
                }
                alt={topic.last_reply_author.full_name}
                className={cn(
                  'w-8 h-8 rounded-full object-cover border',
                  isDarkMode ? 'border-dark-400' : 'border-gray-200'
                )}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
